import { defineEnum, type EnumValueOf } from './define.ts'

/**
 * The enums describing how someone gets into the system.
 *
 * Both exist because `packages/db` may not invent an enum of its own: a `TEXT` + `CHECK`
 * column is built from the literal set defined here, so the constraint and the union
 * cannot drift (`docs/DECISIONS.md`, "Every enum is defined once and emits its own CHECK
 * constraint"). Added by **F03**, which is the task that needed the columns.
 */

/**
 * What a single-use, short-lived account token authorises.
 *
 * `docs/DECISIONS.md` ("Email — none"): there is no mail anywhere, so an invite link and
 * a reset link are strings an admin copies and delivers by hand. Both are therefore
 * account-granting secrets in transit, which is why they are single-use and short-lived
 * rather than the current system's 14-day replayable links — and why they share one
 * table with one lifecycle instead of two half-remembered ones.
 */
export const authTokenPurpose = defineEnum('AuthTokenPurpose', ['invite', 'password_reset'])
export type AuthTokenPurpose = EnumValueOf<typeof authTokenPurpose>

/**
 * Where a self-service access request has got to.
 *
 * `docs/DECISIONS.md` ("Self-service access requests — built for real"): a request is
 * stored, surfaced to that tenant's admins as a pending queue, and an admin converts it
 * into an invite. `approved` is that conversion — the row keeps pointing at the user it
 * produced — and `rejected` is the deliberate refusal, which is a different fact from a
 * request nobody has looked at yet.
 */
export const accessRequestStatus = defineEnum('AccessRequestStatus', [
  'pending',
  'approved',
  'rejected',
])
export type AccessRequestStatus = EnumValueOf<typeof accessRequestStatus>

/**
 * Why a sign-in was ended.
 *
 * `sessions.revoked_reason` is a plain `TEXT` column with **no** `CHECK` constraint, which
 * is unusual here and deliberate: the reasons are an operational vocabulary that later
 * tasks extend (B02 disabling a user, A03's platform-admin realm), and every addition to a
 * constrained column costs a migration. Defining the set here anyway means the application
 * still cannot write a typo, and a reader has one list to consult. Added by **A01**, which
 * is the task that first ends a session.
 *
 * - `logout` — the user asked, on this device.
 * - `rotation_replay` — an already-redeemed refresh token was presented again. The whole
 *   chain dies, because the only two explanations are a stolen token and a client bug, and
 *   the safe response to both is the same (`docs/DECISIONS.md`, "A replayed refresh token
 *   revokes the session").
 * - `user_disabled`, `user_deleted` — the account stopped being able to authenticate while
 *   the session was open.
 * - `password_changed` — the credential the session was established with no longer exists.
 * - `admin_revoked` — an administrator ended someone else's session deliberately.
 * - `tenant_retired` — **P02's**, and the only reason here that is not about a person.
 *   Retiring a tenant revokes every live session in it, in the same operation
 *   (`docs/DECISIONS.md` open question 9, answered by D04 and implemented by P02).
 *   Without it, "retired" would mean a company whose staff keep working for up to thirty
 *   days on a session they already hold, which is a sentence no confirmation dialog can
 *   make sound intentional. It is its own value rather than `admin_revoked` because the
 *   two answer different questions in an audit: one says an administrator ended this
 *   sign-in, the other says the tenancy ended underneath it.
 */
export const sessionRevocationReason = defineEnum('SessionRevocationReason', [
  'logout',
  'rotation_replay',
  'user_disabled',
  'user_deleted',
  'password_changed',
  'admin_revoked',
  'tenant_retired',
])
export type SessionRevocationReason = EnumValueOf<typeof sessionRevocationReason>
